import React, { useEffect, useRef, useState } from 'react';
import type { AiActivitySummary } from '../../../../../shared/lib/buildStatusTracker';
import { sortToolKindKeys, totalAiFromSummary } from './ai-activity-utils';

function snapshotSummary(s: AiActivitySummary): AiActivitySummary {
    return {
        thinking: s.thinking,
        aiText: s.aiText,
        toolByKind: { ...s.toolByKind }
    };
}

function computeFlashKeys(prev: AiActivitySummary | null, curr: AiActivitySummary): Set<string> {
    const flash = new Set<string>();
    if (!prev) return flash;
    const tPrev = totalAiFromSummary(prev);
    const tCurr = totalAiFromSummary(curr);
    if (tCurr > tPrev) flash.add('total');
    if (curr.thinking > prev.thinking) flash.add('thinking');
    if (curr.aiText > prev.aiText) flash.add('text');
    const keys = new Set([...Object.keys(prev.toolByKind), ...Object.keys(curr.toolByKind)]);
    for (const k of keys) {
        const a = prev.toolByKind[k] || 0;
        const b = curr.toolByKind[k] || 0;
        if (b > a) flash.add(`tool:${k}`);
    }
    return flash;
}

const CountSpan: React.FC<{ value: number; flash: boolean }> = ({ value, flash }) => (
    <span
        className={
            flash
                ? 'inline-block animate-ai-count-pulse rounded px-0.5 text-amber-200 tabular-nums'
                : 'tabular-nums'
        }
    >
        {value}
    </span>
);

/**
 * Same copy as formatAiActivitySummaryLabel, with per-number flash when counts increase.
 */
export const AiActivitySummaryRichHeader: React.FC<{ summary: AiActivitySummary }> = ({ summary }) => {
    const prevRef = useRef<AiActivitySummary | null>(null);
    const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());

    useEffect(() => {
        const prev = prevRef.current;
        const keys = computeFlashKeys(prev, summary);
        if (keys.size > 0) {
            setFlashKeys(keys);
            const t = setTimeout(() => setFlashKeys(new Set()), 900);
            prevRef.current = snapshotSummary(summary);
            return () => clearTimeout(t);
        }
        prevRef.current = snapshotSummary(summary);
    }, [summary]);

    const total = totalAiFromSummary(summary);

    const positiveToolKeys = sortToolKindKeys(
        Object.keys(summary.toolByKind).filter(k => (summary.toolByKind[k] || 0) > 0)
    );

    const chunks: React.ReactNode[] = [];
    positiveToolKeys.forEach((k, idx) => {
        const n = summary.toolByKind[k] || 0;
        chunks.push(
            <span key={`tool-${k}`}>
                {idx > 0 ? ', ' : ''}
                <CountSpan value={n} flash={flashKeys.has(`tool:${k}`)} />
                {` ${k}`}
            </span>
        );
    });
    if (summary.thinking > 0) {
        const idx = chunks.length;
        chunks.push(
            <span key="thinking">
                {idx > 0 ? ', ' : ''}
                <CountSpan value={summary.thinking} flash={flashKeys.has('thinking')} />
                {' thinking'}
            </span>
        );
    }
    if (summary.aiText > 0) {
        const idx = chunks.length;
        chunks.push(
            <span key="text">
                {idx > 0 ? ', ' : ''}
                <CountSpan value={summary.aiText} flash={flashKeys.has('text')} />
                {' text'}
            </span>
        );
    }

    return (
        <>
            AI Activity (
            <CountSpan value={total} flash={flashKeys.has('total')} />
            {' messages'}
            {chunks.length > 0 ? (
                <>
                    {': '}
                    {chunks}
                </>
            ) : null}
            )
        </>
    );
};
